import { useMemo, memo } from 'react';
import classNames from 'classnames';
import { useNTContext } from '../../NTContext';
import { useNTUtilsContext } from '../../NTUtilsContext';
import { Amounts } from 'types/report';
import { prefixClass } from 'lib/utils';
import { FormatPercentage, FormatTransactionNumbers } from '../ValueFormatters/ValueFormatters';

import './FormatReportValues.scss';
import { useLocationsStore } from 'hooks/useLocationsStore';
import { useReportValueFormatters } from './FormatReportValues.hook';
import { getRequestHeaderStatus, status_map } from 'presentation/components/Queue/utils/askRequestUtils';
import { AskRequestStatus } from '@hone-automation/common';
import { useActiveReportStore } from 'hooks/useActiveReportStore';
import { useQueryState } from 'hooks/useQueryState';
import { HoneReportTypes } from 'domain/models';

export type FormatReportValuesProps = {
  cellData: Amounts;
  cellID: string | null;
  rowID?: string | null;
  totalColumn?: boolean;
  transformedData?: any;
  columnIndex?: number;
};

const FormatReportValues: React.FC<FormatReportValuesProps> = ({
  cellData,
  cellID,
  rowID,
  totalColumn = false,
  transformedData,
  columnIndex,
}) => {
  const prefix = prefixClass('report-values');
  const { filters, budgetAvailable, columns, reportId: globalReportID } = useNTContext();
  const { actions, setActions, capabilities, setCapabilities } = useNTUtilsContext();
  const [consolidated] = useQueryState<string>('consolidated');
  const [audit] = useQueryState<string>('audit');
  const [compareLocations] = useQueryState<string>('compareLocations');
  const currentReport = useActiveReportStore(state => state.currentReport);

  const {
    index,
    date,
    reportId,
    amount,
    amountAVG,
    percent,
    percentAVG,
    budget,
    budgetPercent,
    difference,
    variance,
    varianceAnalisys,
    vizHelpers,
    anomaly,
    trxAnomaly,
    dataRows,
    trxFlagged,
    auditResult,
  } = cellData;

  const { smoothing, anomalies } = filters;
  const currentLocation = useLocationsStore(state => state.currentLocation);
  const allLocations = useLocationsStore(state => state.allLocations);
  const reportPayload = useActiveReportStore(state => state.reportPayload);

  // Check if this is a Vendor Invoices report
  const isVendorInvoicesReport = reportPayload?.templateId === 'Vendor Invoices';
  const transactionCount = dataRows?.length || 0;

  // We decide to apply smoothing if the smoothing filter is active and if amountAVG is not undefined and it does not have the same value as amount
  const mustSmooth = smoothing && !vizHelpers.noValuesToShow && amountAVG !== undefined && amountAVG !== amount;
  const composedAmount = mustSmooth ? amountAVG : amount;
  const composedPercent = mustSmooth ? percentAVG : percent;

  // Right now anomaly is only shown if the smoothing filter is not active and if anomalies is not undefined and it does not have the same value as amount
  const anomalyValue = anomaly && anomaly.anomalyScores[index];
  const showAnomaly =
    capabilities.anomalies &&
    !mustSmooth &&
    !totalColumn &&
    anomalies &&
    columns > 2 &&
    anomalyValue !== 0 &&
    anomalyValue !== undefined;

  // Anomalies calculation
  const trxAnomalyValue = trxAnomaly && trxAnomaly.anomalyScores[index];
  const showtrxAnomaly =
    capabilities.anomalies &&
    anomalies &&
    !totalColumn &&
    trxAnomaly &&
    columns > 3 &&
    trxAnomalyValue !== 0 &&
    trxAnomalyValue !== undefined;
  // Flagging - AskRequest uses 'Open' | 'In Progress' | 'Closed' status format
  const flaggedCellData = trxFlagged && trxFlagged.length > 0 ? trxFlagged : null;
  const mappedStatuses = flaggedCellData?.map(item => item.status) as AskRequestStatus[] | undefined;

  const flaggedCell = getRequestHeaderStatus(mappedStatuses!);

  const flagInfo = {
    // Transaction-level flags have a transaction property, cell-level flags don't
    flaggedTransactions: cellData.trxFlagged?.filter(trx => trx.transaction) || [],
    isFlaggedCell: flaggedCell && cellData.trxFlagged?.some(trx => !trx.transaction),
    fTrxtotal:
      cellData.trxFlagged?.reduce((total, trx) => (trx.transaction ? total + (trx.flaggedAmount ?? 0) : total), 0) || 0,
    totalTrxFlagged: cellData.trxFlagged?.filter(trx => trx.transaction).length || 0,
  };

  // console.log({ flaggedCell });

  const { formatActualsBlock, formatComposedAmount, formatVariance } = useReportValueFormatters({
    cellData,
    smoothing: filters.smoothing,
    showAnomaly,
    showtrxAnomaly,
    composedAmount,
    flaggedCell,
    flagInfo,
    filters,
  });

  // Phase 3: Helper function to resolve the location for flagging using store
  const resolveLocationForFlag = useMemo(() => {
    // Check if flagging is allowed from the report
    if (currentReport?.allowFlagging === false) return undefined;

    // Check if we're in a multi-location side-by-side mode
    const isAggrSideBySide =
      compareLocations && (!consolidated || consolidated === 'false') ? compareLocations.split(',').length > 1 : false;

    if (isAggrSideBySide && transformedData && allLocations) {
      // Extract location from column metadata using the cell's columnIndex
      if (columnIndex !== undefined && transformedData.columns && transformedData.columns[columnIndex]) {
        const column = transformedData.columns[columnIndex];
        if (column.isLocation && !column.consolidated && allLocations) {
          // Phase 3: Use store locations instead of transformedData.locations
          // Find the location that corresponds to this column
          const location = allLocations.find(loc => column.name === loc.name || column.reportId === loc.id);
          return location || currentLocation; // Fall back to current location if no match found
        }
      }
      // For side-by-side mode, always allow flagging with current location as fallback
      return currentLocation;
    }

    // Default to current location for single-location reports
    return currentLocation;
  }, [currentReport?.allowFlagging, compareLocations, transformedData, columnIndex, currentLocation, allLocations]);

  const handleClick = (e: React.MouseEvent, cellData: Amounts) => {
    e.preventDefault();
    if (!capabilities.flaggedTransactions) return;
    if (!resolveLocationForFlag) return;

    setActions({
      ...actions,
      detailsPanel: true,
    });
    setCapabilities({ ...capabilities, charts: true });
  };

  const shouldShowAuditAsBS = reportPayload?.type === HoneReportTypes.BalanceSheet && audit === 'true';

  return (
    <div
      className={classNames(prefix('KS-report-cell'), {
        'extra-height': vizHelpers.isBigHeader,
        smoothing: mustSmooth,
        total: vizHelpers.isTotal,
        budget: budgetAvailable && filters.budget,
      })}
      data-cell-id={cellID}
    >
      {vizHelpers.noValuesToShow ? (
        <div className={classNames(prefix('KS-report-cell'))} data-cell-id={cellID} />
      ) : (
        <>
          {flaggedCell && (
            <div
              className={classNames(prefix('flagged'), {
                open: flaggedCell === 'Open',
                inProgress: flaggedCell === 'In Progress',
                closed: flaggedCell === 'Closed',
              })}
              /* onClick={e => handleClick(e, cellData)} */
            ></div>
          )}
          {filters.actuals &&
            formatActualsBlock(
              <div
                className={classNames(prefix('actual-container'), {
                  percentage: filters.percentage && filters.money,
                  anomaly: showAnomaly,
                  'empty-cell': cellData.amount === 0 && filters.percentage && filters.money,
                  ['anomaly-positive']: showAnomaly && anomalyValue > 0,
                })}
                data-cell-id={cellID}
              >
                {capabilities.money &&
                  filters.money &&
                  formatComposedAmount(
                    null,
                    false,
                    transactionCount,
                    resolveLocationForFlag ? e => handleClick(e, cellData) : undefined
                  )}
                {capabilities.percentage &&
                  filters.percentage &&
                  FormatPercentage(
                    composedPercent,
                    vizHelpers.isTotal,
                    shouldShowAuditAsBS,
                    auditResult,
                    undefined,
                    cellData.amount !== 0 || flagInfo.flaggedTransactions.length > 0
                  )}
                {(isVendorInvoicesReport || shouldShowAuditAsBS) &&
                  transactionCount > 0 &&
                  FormatTransactionNumbers(
                    transactionCount,
                    vizHelpers.isTotal,
                    shouldShowAuditAsBS,
                    auditResult,
                    resolveLocationForFlag ? e => handleClick(e, cellData) : undefined
                  )}
              </div>
            )}
          {vizHelpers.isTotal && audit === 'true' && <div className={prefix('audit-active')}></div>}
          {capabilities.budget && budgetAvailable && filters.budget && filters.money && date !== 'Difference' && (
            <div
              className={classNames(prefix('actual-container'), {
                percentage: filters.percentage && filters.money,
                budget: true,
                'no-separator': !filters.actuals,
              })}
              data-cell-id={cellID}
            >
              {capabilities.money && filters.money && formatComposedAmount(budget)}
              {capabilities.percentage && filters.percentage && FormatPercentage(budgetPercent)}
            </div>
          )}
          {capabilities.budget &&
            budgetAvailable &&
            filters.budget &&
            filters.actuals &&
            filters.variance &&
            formatVariance(
              <div
                className={classNames(prefix('variance-container'), {
                  percentage: filters.percentage && filters.money,
                  anomaly: varianceAnalisys && varianceAnalisys.tendency === 'negative',
                  ['anomaly-positive']: varianceAnalisys && varianceAnalisys.tendency === 'positive',
                })}
                data-cell-id={cellID}
              >
                {capabilities.money && filters.money && formatComposedAmount(difference, true)}
                {capabilities.percentage && filters.percentage && FormatPercentage(variance)}
              </div>
            )}
        </>
      )}
    </div>
  );
};

// Memoize to prevent re-renders when parent re-renders (e.g., on scroll)
export default memo(FormatReportValues);
